import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Cliente del módulo `accessControl` del API (/access-control/*): sitios,
// puntos de acceso, eventos, accesos temporales, cámaras, vistas y alertas.
// La compañía viaja en `x-company-id` (lo pone el interceptor de utils/api).
// ---------------------------------------------------------------------------

export type EntityName = "sites" | "points" | "events" | "passes" | "cameras" | "views" | "alerts";

export interface AcSite {
  id: string;
  name: string;
  city?: string;
  address?: string;
  type?: "yard" | "warehouse" | "office" | "facility" | string;
  yard_id?: string | null;
  status?: string;
}

export type PointType = "door" | "turnstile" | "barrier" | "gate" | "elevator";
export type PointStatus = "online" | "offline" | "maintenance" | "alarm";
export type AccessMethod = "card" | "face" | "qr" | "plate" | "pin" | "fingerprint" | "manual";

export interface AcPoint {
  id: string;
  name: string;
  site_id: string;
  site_name?: string;
  zone?: string;
  type: PointType;
  direction?: "entry" | "exit" | "bidirectional";
  status: PointStatus;
  methods?: AccessMethod[];
  device_model?: string | null;
  device_serial?: string | null;
  camera_id?: string | null;
  anti_passback?: boolean;
  schedule?: string | null;
  notes?: string | null;
  last_event_at?: string | null;
}

export interface AcEvent {
  id: string;
  site_id?: string | null;
  site_name?: string;
  point_id?: string | null;
  point_name?: string;
  subject_type: "person" | "vehicle";
  subject_name?: string;
  method: AccessMethod;
  result: "granted" | "denied";
  direction?: "entry" | "exit";
  plate?: string | null;
  card_number?: string | null;
  pass_id?: string | null;
  reason?: string | null;
  camera_id?: string | null;
  snapshot_url?: string | null;
  occurred_at: string;
}

export type PassStatus = "active" | "scheduled" | "used" | "expired" | "revoked";

export interface AcPass {
  id: string;
  code: string;
  qr_token: string;
  visitor_name: string;
  visitor_company?: string | null;
  visitor_phone?: string | null;
  visitor_email?: string | null;
  host_name: string;
  host_unit?: string | null;
  reason?: string | null;
  site_id?: string | null;
  site_name?: string;
  point_ids?: string[];
  valid_from: string;
  valid_until: string;
  max_uses: number;
  uses_count: number;
  vehicle_plate?: string | null;
  id_photo_url?: string | null;
  notes?: string | null;
  status: "active" | "revoked";
  effective_status: PassStatus;
  last_used_at?: string | null;
  revoked_at?: string | null;
  createdAt?: string;
}

export type CameraStatus = "online" | "offline" | "recording" | "maintenance";

export interface AcCamera {
  id: string;
  name: string;
  site_id: string;
  site_name?: string;
  zone?: string;
  brand?: string;
  model?: string;
  type?: "dome" | "bullet" | "ptz" | "fisheye" | "turret";
  ip_masked?: string | null;
  channel?: number;
  nvr_name?: string;
  serial?: string;
  status: CameraStatus;
  resolution?: string;
  fps?: number;
  thumbnail_url?: string | null;
  snapshot_url?: string | null;
  /** El API nunca manda la URL del stream en listas o fichas: sólo si existe. */
  has_stream?: boolean;
  ptz?: boolean;
  audio?: boolean;
  night_vision?: boolean;
  recording?: boolean;
  point_id?: string | null;
  last_seen_at?: string | null;
  notes?: string | null;
}

export interface AcView {
  id: string;
  name: string;
  layout: 1 | 4 | 8 | 12;
  camera_ids: string[];
  site_id?: string | null;
  description?: string;
}

export type AlertSeverity = "critical" | "high" | "medium" | "low";
export type AlertStatus = "open" | "acknowledged" | "resolved";

export interface AcAlert {
  id: string;
  type: string;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  description?: string;
  site_id?: string | null;
  site_name?: string;
  camera_id?: string | null;
  camera_name?: string;
  point_id?: string | null;
  snapshot_url?: string | null;
  occurred_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  resolved_at?: string | null;
  assigned_to?: string | null;
  resolution_notes?: string | null;
}

export interface AcSummary {
  sites: number;
  points: { total: number; byStatus: Record<string, number>; byType: Record<string, number> };
  cameras: { total: number; online: number; offline: number; maintenance: number; bySite: Record<string, number> };
  events: {
    last24h: number;
    granted24h: number;
    denied24h: number;
    byMethod24h: Record<string, number>;
    bySubject24h: Record<string, number>;
    hourly: { hour: string; granted: number; denied: number }[];
  };
  passes: { total: number; byStatus: Record<PassStatus, number>; expectedToday: number };
  alerts: {
    total: number;
    open: number;
    critical: number;
    bySeverityOpen: Record<string, number>;
    byStatus: Record<string, number>;
    daily: { day: string; count: number; critical: number }[];
  };
  recentEvents: AcEvent[];
  recentAlerts: AcAlert[];
}

interface EntityMap {
  sites: AcSite;
  points: AcPoint;
  events: AcEvent;
  passes: AcPass;
  cameras: AcCamera;
  views: AcView;
  alerts: AcAlert;
}

const BASE = "/access-control";

export async function listEntity<E extends EntityName>(entity: E, params: Record<string, string | number | undefined> = {}): Promise<EntityMap[E][]> {
  const res = await api.get(`${BASE}/${entity}`, { params: { limit: 1000, ...params } });
  return (res.data?.data ?? []) as EntityMap[E][];
}

export async function createEntity<E extends EntityName>(entity: E, body: Partial<EntityMap[E]>): Promise<EntityMap[E]> {
  const res = await api.post(`${BASE}/${entity}`, body);
  return res.data?.data as EntityMap[E];
}

export async function updateEntity<E extends EntityName>(entity: E, id: string, body: Partial<EntityMap[E]>): Promise<EntityMap[E]> {
  const res = await api.put(`${BASE}/${entity}/${encodeURIComponent(id)}`, body);
  return res.data?.data as EntityMap[E];
}

export async function deleteEntity(entity: EntityName, id: string): Promise<void> {
  await api.delete(`${BASE}/${entity}/${encodeURIComponent(id)}`);
}

export async function getSummary(): Promise<AcSummary> {
  const res = await api.get(`${BASE}/summary`);
  return res.data?.data as AcSummary;
}

export async function revokePass(id: string): Promise<AcPass> {
  const res = await api.post(`${BASE}/passes/${encodeURIComponent(id)}/revoke`);
  return res.data?.data as AcPass;
}

/**
 * URL de reproducción de una cámara (sólo administradores; sin credenciales).
 * Devuelve null si no hay stream o si quien llama no tiene permiso.
 */
export async function getCameraStream(id: string): Promise<string | null> {
  try {
    const res = await api.get(`${BASE}/cameras/${encodeURIComponent(id)}/stream`);
    return (res.data?.data?.stream_url as string | null) ?? null;
  } catch {
    return null;
  }
}

export function useCameraStream(id: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["access-control", "stream", id],
    queryFn: () => getCameraStream(id as string),
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
  });
}

// --- Hooks ---------------------------------------------------------------------

export const acKeys = {
  all: ["access-control"] as const,
  list: (entity: EntityName, params?: Record<string, unknown>) => ["access-control", entity, params ?? {}] as const,
  summary: ["access-control", "summary"] as const,
};

export function useAcList<E extends EntityName>(
  entity: E,
  params?: Record<string, string | number | undefined>,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: acKeys.list(entity, params),
    queryFn: () => listEntity(entity, params),
    staleTime: 15_000,
    refetchInterval: options.refetchInterval,
  });
}

export function useAcSummary(options: { refetchInterval?: number } = {}) {
  return useQuery({ queryKey: acKeys.summary, queryFn: getSummary, staleTime: 15_000, refetchInterval: options.refetchInterval });
}

/** Alta / cambio / baja de una entidad; invalida todo el módulo al terminar. */
export function useAcMutations<E extends EntityName>(entity: E) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: acKeys.all });
  const create = useMutation({ mutationFn: (body: Partial<EntityMap[E]>) => createEntity(entity, body), onSuccess: invalidate });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<EntityMap[E]> }) => updateEntity(entity, id, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => deleteEntity(entity, id), onSuccess: invalidate });
  return { create, update, remove };
}

export function useRevokePass() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: revokePass, onSuccess: () => qc.invalidateQueries({ queryKey: acKeys.all }) });
}

// --- Controladores Hikvision (módulo hikvisionAccess, Patio de maniobras) -------

export interface HikDeviceLite {
  id: string;
  device_name: string;
  device_serial: string;
  yard_id: string;
  yard_name: string | null;
  door_no: number;
  door_type: "entry" | "exit" | "bidirectional";
  controller_model?: string | null;
  readers?: { reader_no: number; name: string; direction: "entry" | "exit" }[];
  status: "online" | "offline" | string;
  connection_mode?: string;
  last_event_at?: string | { _seconds: number } | null;
}

export async function listHikDevices(): Promise<HikDeviceLite[]> {
  const res = await api.get("/hikvisionDeviceList", { params: { limit: 200, page: 1 } });
  return (res.data?.data ?? []) as HikDeviceLite[];
}

export function useHikDevices() {
  return useQuery({ queryKey: ["access-control", "hikvisionDevices"], queryFn: listHikDevices, staleTime: 30_000 });
}

/** Fecha de Firestore serializada (`{_seconds}`) o ISO → ISO. */
export function isoOf(v: HikDeviceLite["last_event_at"]): string | null {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v._seconds === "number") return new Date(v._seconds * 1000).toISOString();
  return null;
}
